import { createFileRoute, Navigate, useSearch, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  X,
  Building,
  Calendar,
  Shield,
  Users as UsersIcon,
  Palette,
  Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { format } from "date-fns";

export const Route = createFileRoute("/app/staff/users")({ component: StaffUsers });

function StaffUsers() {
  const { profile } = useAuth();
  const [users, setUsers] = useState<
    Array<{
      id: string;
      full_name: string | null;
      email: string | null;
      role: string;
      created_at: string;
      customer_id: string | null;
      company_name: string | null;
      brand: string | null;
      teams_enabled: boolean;
      white_label_enabled: boolean;
      white_label_name: string | null;
      white_label_tagline: string | null;
      white_label_logo_url: string | null;
      white_label_primary_color: string | null;
      white_label_accent_color: string | null;
    }>
  >([]);
  const [roleFilter, setRoleFilter] = useState<"all" | "staff" | "customer">("all");
  const [selectedProfile, setSelectedProfile] = useState<{
    id: string;
    full_name: string | null;
    email: string | null;
    role: string;
    created_at: string;
    customer_id: string | null;
    company_name: string | null;
    brand: string | null;
    teams_enabled: boolean;
    white_label_enabled: boolean;
    white_label_name: string | null;
    white_label_tagline: string | null;
    white_label_logo_url: string | null;
    white_label_primary_color: string | null;
    white_label_accent_color: string | null;
  } | null>(null);
  const [isCustomerSettingsOpen, setIsCustomerSettingsOpen] = useState(false);
  const [recentJobs, setRecentJobs] = useState<
    Array<{
      id: string;
      job_title: string;
      status: string;
      created_at: string;
      product_type: string | null;
      quantity: number | null;
    }>
  >([]);
  const [loadingRecentJobs, setLoadingRecentJobs] = useState(false);

  // Get query params for auto-opening customer panel
  const search = useSearch({ from: "/app/staff/users" });
  const customerEmailParam = (search as { email?: string }).email;
  const customerIdParam = (search as { id?: string }).id;

  // Load profiles on mount
  useEffect(() => {
    supabase
      .from("profiles")
      .select(
        "id, full_name, email, role, created_at, customer_id, brand, teams_enabled, white_label_enabled, white_label_name, white_label_tagline, white_label_logo_url, customers(company_name)",
      )
      .then(({ data, error }) => {
        if (error) {
          console.error("Error fetching profiles:", error);
          // Fallback query without customers join if it fails
          supabase
            .from("profiles")
            .select(
              "id, full_name, email, role, created_at, customer_id, brand, teams_enabled, white_label_enabled, white_label_name, white_label_tagline, white_label_logo_url",
            )
            .then(({ data: fallbackData, error: fallbackError }) => {
              if (fallbackError) {
                console.error("Error fetching profiles (fallback):", fallbackError);
              } else {
                setUsers(
                  ((fallbackData ?? []) as any[]).map((u) => ({
                    ...u,
                    company_name: null,
                    white_label_name: u.white_label_name ?? null,
                    white_label_tagline: u.white_label_tagline ?? null,
                    white_label_logo_url: u.white_label_logo_url ?? null,
                    white_label_primary_color: u.white_label_primary_color ?? null,
                    white_label_accent_color: u.white_label_accent_color ?? null,
                  })),
                );
              }
            });
        } else {
          // Map the nested customers fields to flat properties
          const mappedData = ((data ?? []) as any[]).map((u) => ({
            ...u,
            company_name: u.customers?.company_name || null,
            brand: u.brand || null,
            teams_enabled: u.teams_enabled || false,
            white_label_enabled: u.white_label_enabled || false,
            white_label_name: u.white_label_name ?? null,
            white_label_tagline: u.white_label_tagline ?? null,
            white_label_logo_url: u.white_label_logo_url ?? null,
            white_label_primary_color: (u as any).white_label_primary_color ?? null,
            white_label_accent_color: (u as any).white_label_accent_color ?? null,
          }));
          setUsers(mappedData);

          // Auto-open customer panel if query param is present
          if ((customerEmailParam || customerIdParam) && mappedData) {
            const targetUser = mappedData.find(
              (u) =>
                (customerEmailParam && u.email === customerEmailParam) ||
                (customerIdParam && u.id === customerIdParam),
            );
            if (targetUser && targetUser.role === "customer") {
              handleOpenCustomerSettings(targetUser);
            }
          }
        }
      });
  }, [customerEmailParam, customerIdParam]);

  const handleOpenCustomerSettings = (user: typeof selectedProfile) => {
    if (!user) return;
    setSelectedProfile(user);
    setIsCustomerSettingsOpen(true);

    // Fetch recent jobs for this customer
    if (user.role === "customer") {
      setLoadingRecentJobs(true);
      supabase
        .from("jobs")
        .select("id, job_title, status, created_at, product_type, quantity")
        .eq("customer_id", user.customer_id!)
        .order("created_at", { ascending: false })
        .limit(5)
        .then(({ data, error }) => {
          if (error) {
            console.error("Error fetching recent jobs:", error);
          } else {
            setRecentJobs(data ?? []);
          }
          setLoadingRecentJobs(false);
        });
    } else {
      setRecentJobs([]);
    }
  };

  const updateProfileField = async (
    profileId: string,
    patch: Partial<{
      teams_enabled: boolean;
      white_label_enabled: boolean;
      brand: string | null;
      full_name: string;
      white_label_name: string | null;
      white_label_tagline: string | null;
      white_label_logo_url: string | null;
      white_label_primary_color: string | null;
      white_label_accent_color: string | null;
    }>,
    successMsg: string,
  ) => {
    const { error } = await supabase
      .from("profiles")
      .update(patch as any)
      .eq("id", profileId);
    if (error) {
      console.error("profile update error", error);
      toast.error(error.message || "Update failed");
      return false;
    }
    setUsers((prev) => prev.map((u) => (u.id === profileId ? { ...u, ...patch } : u)));
    setSelectedProfile((prev) => (prev && prev.id === profileId ? { ...prev, ...patch } : prev));
    toast.success(successMsg);
    return true;
  };

  const filteredUsers = users.filter((u) => {
    const roleMatch = roleFilter === "all" || u.role === roleFilter;
    return roleMatch;
  });

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <SettingsIcon className="h-5 w-5" /> Users
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          View all FlightDeck users. Manage customer features such as Teams, white labelling, and
          brand assignments.
        </p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <select
            className="rounded-full border border-border bg-background px-3 py-1 text-sm"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as "all" | "staff" | "customer")}
          >
            <option value="all">All roles</option>
            <option value="staff">Staff</option>
            <option value="customer">Customer</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-medium">Name</th>
              <th className="text-left py-3 px-4 font-medium">Company</th>
              <th className="text-left py-3 px-4 font-medium">Role</th>
              <th className="text-left py-3 px-4 font-medium">Brand</th>
              <th className="text-left py-3 px-4 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter((user) => {
                const roleMatch = roleFilter === "all" || user.role === roleFilter;
                return roleMatch;
              })
              .map((user) => {
                // Determine primary text and subtext
                let primaryText: string;
                let subtext: string;

                if (user.role === "staff") {
                  primaryText = user.full_name || "Staff user";
                } else {
                  primaryText = user.full_name || user.company_name || "Unknown";
                }
                subtext = user.email || "No email";

                const isMe = user.id === profile?.id;
                return (
                  <tr
                    key={user.id}
                    className={`border-b border-border cursor-pointer hover:bg-muted/50 ${isMe ? "bg-primary/5" : ""}`}
                    onClick={() => handleOpenCustomerSettings(user)}
                  >
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="font-medium flex items-center gap-2">
                          {primaryText}
                          {isMe && (
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary border border-primary/20">
                              you
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">{subtext}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {user.role === "staff" ? "—" : user.company_name || "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          user.role === "staff"
                            ? "bg-primary/10 text-primary"
                            : "bg-accent/10 text-accent"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">{user.brand || "—"}</td>
                    <td className="py-3 px-4">{new Date(user.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Customer Settings Panel */}
      {isCustomerSettingsOpen && selectedProfile && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsCustomerSettingsOpen(false)}
          />

          {/* Panel */}
          <div className="fixed right-0 top-0 h-full w-full md:max-w-md bg-background border-l border-border z-50 shadow-2xl overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold">
                    Settings for {selectedProfile.full_name ?? selectedProfile.email ?? "Customer"}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Staff-only controls for this customer's FlightDeck account.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsCustomerSettingsOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Customer Overview */}
              <div className="rounded-3xl border border-border bg-gradient-soft p-5 space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Building className="h-4 w-4" /> Profile
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <label className="text-xs text-muted-foreground">Full name</label>
                    <input
                      className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                      defaultValue={selectedProfile.full_name ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== (selectedProfile.full_name ?? "")) {
                          updateProfileField(selectedProfile.id, { full_name: v }, "Name updated");
                        }
                      }}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Email</div>
                    <div className="font-medium">{selectedProfile.email ?? "—"}</div>
                  </div>
                  {selectedProfile.role !== "staff" && (
                    <div>
                      <div className="text-xs text-muted-foreground">Company</div>
                      <div className="font-medium">{selectedProfile.company_name ?? "—"}</div>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-muted-foreground">Brand</label>
                    <select
                      className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                      value={selectedProfile.brand ?? ""}
                      onChange={(e) =>
                        updateProfileField(
                          selectedProfile.id,
                          { brand: e.target.value || null },
                          "Brand updated",
                        )
                      }
                    >
                      <option value="">— None —</option>
                      <option value="resolute">Resolute</option>
                      <option value="craftyjet">CraftyJet</option>
                    </select>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Created</div>
                    <div className="font-medium">
                      {new Date(selectedProfile.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Toggles */}
              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Palette className="h-4 w-4" /> Features
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">Teams</div>
                      <div className="text-xs text-muted-foreground">
                        Enable team collaboration features
                      </div>
                    </div>
                    <Switch
                      checked={selectedProfile.teams_enabled}
                      onCheckedChange={(checked) =>
                        updateProfileField(
                          selectedProfile.id,
                          { teams_enabled: checked },
                          checked ? "Teams enabled" : "Teams disabled",
                        )
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">White Label</div>
                      <div className="text-xs text-muted-foreground">
                        Enable white label branding
                      </div>
                    </div>
                    <Switch
                      checked={selectedProfile.white_label_enabled}
                      onCheckedChange={(checked) =>
                        updateProfileField(
                          selectedProfile.id,
                          { white_label_enabled: checked },
                          checked ? "White label enabled" : "White label disabled",
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              {/* White Label Branding */}
              {selectedProfile?.white_label_enabled && (
                <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Palette className="h-4 w-4" /> White label branding
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Customise what this customer sees in their portal — logo, app name, and tagline.
                  </p>
                  <div className="space-y-3 text-sm">
                    <div>
                      <label className="text-xs text-muted-foreground">App name</label>
                      <input
                        className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                        defaultValue={selectedProfile.white_label_name ?? ""}
                        placeholder="e.g. MyPrint Portal"
                        onBlur={(e) => {
                          const v = e.target.value.trim() || null;
                          updateProfileField(
                            selectedProfile.id,
                            { white_label_name: v },
                            "App name updated",
                          );
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Tagline</label>
                      <input
                        className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                        defaultValue={selectedProfile.white_label_tagline ?? ""}
                        placeholder="e.g. Print smarter, not harder"
                        onBlur={(e) => {
                          const v = e.target.value.trim() || null;
                          updateProfileField(
                            selectedProfile.id,
                            { white_label_tagline: v },
                            "Tagline updated",
                          );
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Logo URL</label>
                      <input
                        className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                        defaultValue={selectedProfile.white_label_logo_url ?? ""}
                        placeholder="https://example.com/logo.png"
                        onBlur={(e) => {
                          const v = e.target.value.trim() || null;
                          updateProfileField(
                            selectedProfile.id,
                            { white_label_logo_url: v },
                            "Logo URL updated",
                          );
                        }}
                      />
                      {selectedProfile.white_label_logo_url && (
                        <img
                          src={selectedProfile.white_label_logo_url}
                          alt="White label logo preview"
                          className="mt-2 h-10 w-auto rounded border border-border bg-muted p-1"
                        />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Primary colour</label>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="color"
                            className="h-8 w-10 cursor-pointer rounded border border-border bg-background p-0.5"
                            value={selectedProfile.white_label_primary_color ?? "#3b82f6"}
                            onChange={(e) =>
                              updateProfileField(
                                selectedProfile.id,
                                { white_label_primary_color: e.target.value },
                                "Primary colour updated",
                              )
                            }
                          />
                          <span className="text-xs font-mono text-muted-foreground">
                            {selectedProfile.white_label_primary_color ?? "—"}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Accent colour</label>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="color"
                            className="h-8 w-10 cursor-pointer rounded border border-border bg-background p-0.5"
                            value={selectedProfile.white_label_accent_color ?? "#8b5cf6"}
                            onChange={(e) =>
                              updateProfileField(
                                selectedProfile.id,
                                { white_label_accent_color: e.target.value },
                                "Accent colour updated",
                              )
                            }
                          />
                          <span className="text-xs font-mono text-muted-foreground">
                            {selectedProfile.white_label_accent_color ?? "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Jobs */}
              {selectedProfile?.role === "customer" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="rounded-3xl border border-border bg-gradient-soft p-5 space-y-4"
                >
                  <h4 className="font-semibold flex items-center gap-2">
                    <UsersIcon className="h-4 w-4" /> Recent jobs for this customer
                  </h4>
                  {loadingRecentJobs ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
                      <span className="text-sm text-muted-foreground">Loading recent jobs…</span>
                    </div>
                  ) : recentJobs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No jobs found for this customer yet.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {recentJobs.map((job) => (
                        <li key={job.id}>
                          <Link
                            to="/app/staff/jobs/$id"
                            params={{ id: job.id }}
                            className="flex items-center justify-between rounded-xl bg-card p-3 hover:bg-muted transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{job.job_title}</div>
                              <div className="text-xs text-muted-foreground">
                                {job.product_type}
                                {job.quantity ? `, ${job.quantity}` : ""} •{" "}
                                {format(new Date(job.created_at), "d MMM yyyy")}
                              </div>
                            </div>
                            <div className="ml-3">
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                  job.status === "Completed"
                                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                    : job.status === "On Hold"
                                      ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                                      : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                }`}
                              >
                                {job.status}
                              </span>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}

              {/* Security */}
              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
                <h4 className="font-medium">Security</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  If this customer needs a password reset, ask them to use "Forgot password" on the
                  login page. Soon, staff will be able to trigger a reset email directly from here.
                </p>
                <Button disabled className="mt-4 rounded-full">
                  Send reset email
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
